/* eslint-disable react/no-unescaped-entities */
/**
 * Public sales page for ANY coaching program by slug — DB-backed.
 * Route: /coaching/:slug (all three trilogy programs render through this
 * one component; TheMeISeeSalesPage.jsx is retired in favor of this).
 *
 * Visual language mirrors /coaching/identity/*: royal-purple + gold
 * editorial palette, serif-forward Playfair headlines, gold-underline
 * key words, portrait treatment, generous negative space.
 *
 * NOTE: this page shows program facts + curriculum but does NOT handle
 * payment. The primary CTA routes to /coaching/{slug}/enroll where the
 * intake form + Square Checkout live.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/coaching/programs/public`;

const PALETTE = {
  purple: "#4C1D95",
  purpleSoft: "#7C3AED",
  gold: "#B8924B",
  ink: "#0B0B0F",
  paper: "#FAF8F4",
};

const LOGO_CV =
  "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/v3yr3vyl_C.jpg";
const HERO_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_kissed-lite/artifacts/79s8cv89_Dr.%20Christy%20Vonta%202.png";

const PROGRAM_HERO = {
  "the-me-i-see": {
    eyebrow: "Program 01 · The Entry",
    subhead: (
      <>
        Eight weeks of <GoldWord>identity</GoldWord> healing. The woman you were
        told to be… meets the woman you were <GoldWord>meant</GoldWord> to be.
      </>
    ),
    voice_quote:
      '"Every woman is walking around wearing a name that isn\'t hers. This is where you take yours back."',
    cta: "Begin The Me I See",
  },
  "healing-what-you-cannot-feel": {
    eyebrow: "Program 02 · The Excavation",
    subhead: (
      <>
        Twelve weeks in the rooms of the body where <GoldWord>grief</GoldWord>{" "}
        lives. Not to relive it — to <GoldWord>release</GoldWord> it.
      </>
    ),
    voice_quote:
      '"The pain you refuse to feel is running your life. Feel it. Let it move. Let it leave."',
    cta: "Begin Healing WYCF",
  },
  "she-is": {
    eyebrow: "Program 03 · The Arrival",
    subhead: (
      <>
        Twelve weeks of <GoldWord>embodiment</GoldWord>. She is no longer
        becoming. She has <GoldWord>arrived</GoldWord>.
      </>
    ),
    voice_quote:
      '"You have looked. You have unpacked. You have risen. Now you simply declare it."',
    cta: "Enter She Is",
  },
};

const PAYMENT_LABELS = {
  paid_in_full: "Pay in Full",
  "2_pay": "2-Pay Plan",
  "3_pay": "3-Pay Plan",
  "4_pay": "4-Pay Plan",
  "6_pay": "6-Pay Plan",
  contact_us: "Contact for Custom",
};

function GoldWord({ children }) {
  return (
    <span
      style={{
        borderBottom: `2px solid ${PALETTE.gold}`,
        paddingBottom: 1,
        color: PALETTE.ink,
      }}
    >
      {children}
    </span>
  );
}

export default function CoachingProgramPublic() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useDocumentMeta({
    title: program?.title
      ? `${program.title}™ · Dr. Christy Vonta Coaching`
      : "Coaching Program · Dr. Christy Vonta",
    description:
      program?.subtitle ||
      program?.description ||
      "A signature coaching program from Dr. Christy Vonta — part of the Healing Trilogy.",
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/${slug}`);
        if (!cancel) setProgram(r.data.program);
      } catch {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [slug]);

  if (loading)
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: PALETTE.paper }}
      >
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: PALETTE.purple }} />
      </div>
    );
  if (notFound || !program)
    return (
      <div
        className="min-h-screen flex items-center justify-center text-center p-8"
        style={{ background: PALETTE.paper }}
      >
        <div>
          <div className="font-serif text-3xl text-slate-900">Program not found</div>
          <div className="text-sm text-slate-600 mt-2">
            This program is not currently published.
          </div>
          <Link
            to="/coaching"
            className="mt-4 inline-block underline text-sm"
            style={{ color: PALETTE.purple }}
          >
            ← Back to Coaching
          </Link>
        </div>
      </div>
    );

  const hero = PROGRAM_HERO[slug] || {
    eyebrow: "Signature Program",
    subhead: program.description,
    voice_quote: program.subtitle,
    cta: "Begin Enrollment",
  };
  const moduleCount = program.total_modules ?? (program.modules || []).length;
  const lessonCount =
    program.total_lessons ??
    (program.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0);

  return (
    <div
      className="min-h-screen"
      style={{ background: PALETTE.paper, color: PALETTE.ink }}
      data-testid="coaching-public-page"
    >
      <SharedHeader />

      {/* HERO — editorial two-column */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div
            className="text-[11px] uppercase mb-5"
            style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
            data-testid="program-eyebrow"
          >
            {hero.eyebrow} —
          </div>
          <h1
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
            style={{ color: PALETTE.ink }}
            data-testid="program-title"
          >
            {program.title}
            {program.trademark !== false && (
              <sup className="text-base ml-1 text-slate-400 font-normal">™</sup>
            )}
          </h1>
          <p
            className="mt-6 text-lg leading-relaxed max-w-xl"
            style={{ color: "#332C3A" }}
            data-testid="program-subhead"
          >
            {hero.subhead}
          </p>
          <hr className="mt-8 mb-6" style={{ borderColor: PALETTE.gold, opacity: 0.4, maxWidth: 60 }} />
          <p
            className="text-[15.5px] leading-relaxed max-w-xl"
            style={{ color: "#453f52" }}
          >
            {program.description}
          </p>
          {program.target_audience && (
            <p
              className="mt-4 text-[14px] italic max-w-xl"
              style={{ color: PALETTE.purpleSoft }}
            >
              For: {program.target_audience}
            </p>
          )}
          <div className="mt-9 flex flex-wrap gap-4 items-center">
            <Link
              to={`/coaching/${slug}/enroll`}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm tracking-wide text-white"
              style={{
                background: PALETTE.purple,
                boxShadow: "0 14px 30px -10px rgba(76,29,149,0.45)",
              }}
              data-testid="coaching-cta"
            >
              {hero.cta} <ArrowRight className="w-4 h-4" />
            </Link>
            <span
              className="text-xs"
              style={{ color: PALETTE.gold, letterSpacing: "0.02em" }}
            >
              ✦ {program.duration_weeks} weeks · {moduleCount} modules · $
              {program.price_usd?.toLocaleString?.() || program.price_usd}
            </span>
          </div>
        </div>

        {/* Portrait card with pull-quote overlay */}
        <div className="lg:col-span-5">
          <div
            className="relative rounded-3xl overflow-hidden"
            style={{ boxShadow: "0 30px 60px -30px rgba(76,29,149,0.35)" }}
          >
            <img
              src={HERO_PORTRAIT}
              alt="Dr. Christy Vonta"
              className="w-full h-[420px] sm:h-[520px] object-cover"
            />
          </div>
          <div
            className="mt-4 rounded-2xl p-6"
            style={{
              background: PALETTE.purple,
              color: "#fff",
              boxShadow: "0 14px 30px -12px rgba(76,29,149,0.5)",
            }}
          >
            <p
              className="italic leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 16 }}
            >
              {hero.voice_quote}
            </p>
            <div
              className="mt-3 text-[10px] uppercase"
              style={{ letterSpacing: "0.24em", color: PALETTE.gold, fontWeight: 700 }}
            >
              — Dr. Christy Vonta
            </div>
          </div>
        </div>
      </section>

      {/* FACTS STRIP */}
      <section className="max-w-6xl mx-auto px-6 pb-2">
        <div
          className="grid grid-cols-2 sm:grid-cols-4 rounded-2xl"
          style={{
            background: "#fff",
            border: `1px solid rgba(184,146,75,0.25)`,
            boxShadow: "0 10px 30px -15px rgba(76,29,149,0.12)",
          }}
          data-testid="program-facts"
        >
          <Stat label="Duration" value={`${program.duration_weeks} weeks`} />
          <Stat
            label="Investment"
            value={`$${program.price_usd?.toLocaleString?.() || program.price_usd}`}
          />
          <Stat label="Modules" value={moduleCount} />
          <Stat label="Lessons" value={lessonCount} />
        </div>
      </section>

      {/* PAYMENT OPTIONS */}
      {program.payment_options?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-14">
          <div
            className="text-[11px] uppercase mb-5"
            style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
          >
            Payment Options
          </div>
          <div className="flex flex-wrap gap-3">
            {program.payment_options.map((opt) => (
              <span
                key={opt}
                className="rounded-full text-xs px-4 py-2"
                style={{
                  background: "#fff",
                  border: `1px solid ${PALETTE.gold}`,
                  color: PALETTE.purple,
                  fontWeight: 700,
                }}
              >
                {PAYMENT_LABELS[opt] || opt}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* CURRICULUM (CMS-driven, only if populated) */}
      {(program.modules || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-16">
          <div
            className="text-[11px] uppercase mb-5"
            style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
          >
            Curriculum
          </div>
          <div className="space-y-4">
            {program.modules
              .slice()
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((m) => (
                <div
                  key={m.id}
                  className="rounded-2xl p-6"
                  style={{
                    background: "#fff",
                    border: `1px solid rgba(184,146,75,0.25)`,
                  }}
                >
                  <div
                    className="text-[10px] uppercase"
                    style={{ letterSpacing: "0.2em", color: PALETTE.purpleSoft, fontWeight: 700 }}
                  >
                    Module {m.order}
                  </div>
                  <div
                    className="font-serif text-2xl mt-1"
                    style={{ color: PALETTE.ink }}
                  >
                    {m.title}
                  </div>
                  {m.summary && (
                    <div className="text-sm mt-2" style={{ color: "#453f52" }}>
                      {m.summary}
                    </div>
                  )}
                  {(m.lessons || []).length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-sm">
                      {m.lessons
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((l) => (
                          <li key={l.id} className="flex items-start gap-2">
                            <CheckCircle2
                              className="w-4 h-4 mt-0.5 shrink-0"
                              style={{ color: PALETTE.gold }}
                            />
                            <span style={{ color: "#332C3A" }}>{l.title}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              ))}
          </div>
        </section>
      )}

      {/* CLOSING CTA CARD */}
      <section className="max-w-6xl mx-auto px-6 mt-16 mb-24">
        <div
          className="rounded-[32px] p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
          style={{
            background: "#fff",
            border: `1px solid rgba(184,146,75,0.3)`,
            boxShadow: "0 30px 60px -30px rgba(76,29,149,0.18)",
          }}
        >
          <div className="lg:col-span-8">
            <div
              className="text-[11px] uppercase mb-3"
              style={{ letterSpacing: "0.28em", color: PALETTE.gold, fontWeight: 700 }}
            >
              Ready to begin?
            </div>
            <h3
              className="font-serif text-4xl leading-tight"
              style={{ color: PALETTE.ink }}
            >
              Your seat in {program.title}™ is waiting.
            </h3>
            <p className="mt-3 text-[15px]" style={{ color: "#453f52", maxWidth: 500 }}>
              Enrollment secures your access. The moment your payment clears,
              your welcome email arrives with your passcode-login link.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to={`/coaching/${slug}/enroll`}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm tracking-wide text-white"
              style={{
                background: PALETTE.purple,
                boxShadow: "0 14px 30px -10px rgba(76,29,149,0.45)",
              }}
              data-testid="coaching-cta-bottom"
            >
              <Sparkles className="w-4 h-4" /> {hero.cta}
            </Link>
          </div>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-6 py-6 sm:border-r last:border-r-0" style={{ borderColor: "rgba(184,146,75,0.25)" }}>
      <div
        className="text-[10px] uppercase"
        style={{ letterSpacing: "0.24em", color: PALETTE.gold, fontWeight: 700 }}
      >
        {label}
      </div>
      <div
        className="font-serif text-3xl mt-1"
        style={{ color: PALETTE.ink }}
      >
        {value}
      </div>
    </div>
  );
}

export function SharedHeader() {
  return (
    <header className="border-b" style={{ borderColor: "rgba(184,146,75,0.25)" }}>
      <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/coaching" className="flex items-center gap-3" data-testid="coaching-brand">
          <img
            src={LOGO_CV}
            alt="Dr. Christy Vonta"
            className="w-11 h-11 object-contain rounded-full bg-white border"
            style={{ borderColor: "rgba(184,146,75,0.4)" }}
          />
          <div className="leading-tight">
            <div
              className="font-serif text-[15px]"
              style={{ color: PALETTE.ink }}
            >
              Dr. <i style={{ color: PALETTE.purple }}>Christy Vonta</i>
            </div>
            <div
              className="text-[10px] uppercase"
              style={{ letterSpacing: "0.22em", color: PALETTE.gold, fontWeight: 700 }}
            >
              Coaching &amp; Consulting
            </div>
          </div>
        </Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/coaching" className="hover:opacity-70" style={{ color: PALETTE.ink }}>
            Programs
          </Link>
          <Link
            to="/coaching/identity/assessment"
            className="hover:opacity-70"
            style={{ color: PALETTE.ink }}
            data-testid="header-identity-link"
          >
            Identity Mirror
          </Link>
          <Link
            to="/coaching/login"
            className="hover:opacity-70 text-xs uppercase"
            style={{ letterSpacing: "0.18em", color: PALETTE.purple }}
            data-testid="header-signin"
          >
            Sign In
          </Link>
        </nav>
      </div>
    </header>
  );
}

export function SharedFooter() {
  return (
    <footer
      className="border-t"
      style={{ borderColor: "rgba(184,146,75,0.25)", background: "#fff" }}
    >
      <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8 text-sm">
        <div>
          <div
            className="font-serif text-lg"
            style={{ color: PALETTE.ink }}
          >
            Dr. <i style={{ color: PALETTE.purple }}>Christy Vonta</i>
          </div>
          <div
            className="text-[10px] uppercase mt-1"
            style={{ letterSpacing: "0.22em", color: PALETTE.gold, fontWeight: 700 }}
          >
            Coaching &amp; Consulting
          </div>
        </div>
        <div>
          <div
            className="text-[10px] uppercase"
            style={{ letterSpacing: "0.22em", color: PALETTE.gold, fontWeight: 700 }}
          >
            The Trilogy
          </div>
          <ul className="mt-2 space-y-1.5" style={{ color: PALETTE.ink }}>
            <li><Link to="/coaching/the-me-i-see" className="hover:opacity-70">The Me I See™</Link></li>
            <li><Link to="/coaching/healing-what-you-cannot-feel" className="hover:opacity-70">Healing What You Cannot Feel™</Link></li>
            <li><Link to="/coaching/she-is" className="hover:opacity-70">She Is™</Link></li>
          </ul>
        </div>
        <div>
          <div
            className="text-[10px] uppercase"
            style={{ letterSpacing: "0.22em", color: PALETTE.gold, fontWeight: 700 }}
          >
            Begin
          </div>
          <ul className="mt-2 space-y-1.5" style={{ color: PALETTE.ink }}>
            <li><Link to="/coaching/identity" className="hover:opacity-70">Identity Alignment Assessment</Link></li>
            <li><Link to="/coaching/trilogy/certificate" className="hover:opacity-70">Master Trilogy Certificate</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 pb-8 text-[11px]" style={{ color: "#6B6472" }}>
        © {new Date().getFullYear()} Christy Vonta Coaching &amp; Consulting · KCIS Coaching Door
      </div>
    </footer>
  );
}
